import React, { useState } from 'react'
import type { WatchlistList } from '../../services/watchlist'
import WatchlistCreateListInline from './WatchlistCreateListInline'
import { listTreeRows } from '../../utils/watchlistTree'

interface WatchlistListPickerProps {
  lists: WatchlistList[]
  selectedListIds: number[]
  onChange: (listIds: number[]) => void
  idPrefix?: string
  onCreateList?: (name: string) => Promise<WatchlistList>
  /**
   * False while the entity is not on the watchlist yet: nothing is ticked,
   * and ticking »Ingen kategorisering« adds it without a list.
   */
  watched?: boolean
  /** Lock the checkboxes while a change is being saved. */
  disabled?: boolean
}

/** From this many lists the picker gets a filter field. */
const FILTER_FROM = 7

/**
 * Checkbox list: "Ingen kategorisering" (empty listIds) or one/more project lists.
 * Long list collections scroll inside a fixed height and can be filtered,
 * so the dropdown never grows past the screen.
 */
const WatchlistListPicker: React.FC<WatchlistListPickerProps> = ({
  lists,
  selectedListIds,
  onChange,
  idPrefix = 'wl',
  onCreateList,
  watched = true,
  disabled = false,
}) => {
  const uncategorized = watched && selectedListIds.length === 0
  const [filter, setFilter] = useState('')
  const q = filter.trim().toLocaleLowerCase('da')
  // Sublists sit indented under their parent; a search shows matches flat.
  const shownLists = q
    ? lists
        .filter((l) => l.name.toLocaleLowerCase('da').includes(q))
        .map((list) => ({ list, depth: 0 }))
    : listTreeRows(lists)

  function toggleList(listId: number, checked: boolean) {
    if (checked) {
      onChange([...selectedListIds, listId])
      return
    }
    onChange(selectedListIds.filter((id) => id !== listId))
  }

  return (
    <fieldset
      disabled={disabled}
      aria-label="Vælg lister"
      className="space-y-2 min-w-0 w-full overflow-hidden disabled:opacity-60"
    >
      <label className="flex items-center gap-2 min-w-0 text-sm text-ink cursor-pointer">
        <input
          type="checkbox"
          id={`${idPrefix}-none`}
          checked={uncategorized}
          onChange={() => onChange([])}
          className="shrink-0 rounded-sm border-rule accent-[#12151A]"
        />
        <span className="min-w-0 truncate">Ingen kategorisering</span>
      </label>
      {lists.length >= FILTER_FROM && (
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Find blandt ${lists.length} lister`}
          aria-label="Find liste"
          className="h-8 w-full rounded-md border border-rule-strong bg-paper px-2.5 text-sm text-ink placeholder:text-ink-400"
        />
      )}
      {lists.length > 0 && (
        <ul className="max-h-56 space-y-1.5 overflow-y-auto overscroll-contain pt-1 border-t border-rule min-w-0">
          {shownLists.length === 0 && (
            <li className="py-1 text-[13px] text-ink-500">Ingen lister matcher.</li>
          )}
          {shownLists.map(({ list, depth }) => {
            const checked = selectedListIds.includes(list.id)
            return (
              <li key={list.id} className="min-w-0" style={depth > 0 ? { paddingLeft: depth * 18 } : undefined}>
                <label className="flex items-center gap-2 min-w-0 text-sm text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    id={`${idPrefix}-list-${list.id}`}
                    checked={checked}
                    onChange={(e) => toggleList(list.id, e.target.checked)}
                    className="shrink-0 rounded-sm border-rule accent-[#12151A]"
                  />
                  <span className="min-w-0 flex-1 truncate" title={list.name}>
                    {list.name}
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      )}
      {onCreateList && (
        <div className={`${lists.length > 0 ? 'pt-1' : 'pt-1 border-t border-rule'} min-w-0`}>
          <WatchlistCreateListInline
            compact
            inputId={`${idPrefix}-new-list`}
            onCreate={onCreateList}
            onCreated={(list) => onChange([...selectedListIds, list.id])}
          />
        </div>
      )}
    </fieldset>
  )
}

export default WatchlistListPicker
